import { setCookie } from '../../utils/utils';
import {
  AnalyticsStepData,
  ApplicationData,
  HighEntropyValues,
  NavigatorWithUAData,
  UserAgentData,
  isAnalyticsStepData,
} from './piano-analytics.types';

const pageSection = 'rbo app';
const pageTemplateType = 'rfs';

export const generateId = () => {
  let randomString = new Date().getTime().toString(36);
  while (randomString.length < 16) {
    randomString += Math.round(Math.random() * 2_147_483_647).toString(36);
  }

  return randomString.slice(0, 16);
};

export const stringToDate = (date: string | null): Date | null => {
  if (!date) {
    return null;
  }
  const parsedValue = parseInt(date, 36);
  if (isNaN(parsedValue)) {
    return null;
  }
  const parsedDate = new Date(parsedValue);
  if (parsedDate instanceof Date && !isNaN(parsedDate.valueOf())) {
    return parsedDate;
  }
  return null;
};

const getCookieValue = (cookieName: string): string | null => {
  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${cookieName}=`));
  return cookie ? cookie.split('=')[1] : null;
};

const getPcidCookie = () => getCookieValue('_pcid');
const raboLangCookie = () => getCookieValue('RABOLANG');
const consentCookie = () => getCookieValue('_pprv');
const visitorPrivacyMode = () => getCookieValue('pa_privacy');
const pnpCookie = () => getCookieValue('PNP');

export const getCookieDate = () => {
  const pcidCookie = getPcidCookie();
  if (!pcidCookie) {
    return null;
  }
  try {
    const pcidString: string = decodeURIComponent(pcidCookie);
    const pcidData = JSON.parse(pcidString) as { _t?: string };
    const expiration = pcidData?._t;
    if (expiration) {
      const splitData = expiration.split('|');
      const fixedExpirationDate = stringToDate(splitData[0]);
      const fixedCreationDate = stringToDate(splitData[1]);
      const fixedMode = !!fixedExpirationDate;
      return fixedMode ? [fixedCreationDate, fixedExpirationDate] : null;
    }
  } catch (e) {
    console.error('Error parsing PCID cookie JSON:', e);
  }
  return null;
};

export const cookieDate = getCookieDate();

export const getCookieCreationDate = () => {
  const pcidData = getCookieDate();
  try {
    if (pcidData?.[0]) {
      return new Date(new Date(pcidData[0]).setMilliseconds(0)).toISOString();
    }
  } catch (e) {
    console.error('Error parsing cookie creation date:', e);
  }
  return null;
};

export const getBrowserId = (): string => {
  const pcidCookie = getPcidCookie();
  if (pcidCookie) {
    try {
      const pcidString: string = decodeURIComponent(pcidCookie);
      const parsedValue = JSON.parse(pcidString) as { browserId?: string };
      if (!parsedValue?.browserId) {
        throw new Error('Browser ID not found in cookie');
      }
      return parsedValue.browserId;
    } catch (error) {
      console.error('Error parsing PCID cookie JSON:', error);
    }
  }
  const newBrowserId = generateId();
  const creationDateString = new Date().getTime().toString(36);
  const expirationDate = new Date();
  expirationDate.setFullYear(expirationDate.getFullYear() + 1);
  const expirationDateString = expirationDate.getTime().toString(36);

  setCookie(
    '_pcid',
    JSON.stringify({
      browserId: newBrowserId,
      _t: `${expirationDateString}|${creationDateString}`,
    }),
  );
  return newBrowserId;
};

export const browserId = getBrowserId();

const browserLanguage = (() => {
  const lang = navigator.language || 'en';
  const splitLang = lang.split(/[-_]/);
  return {
    browser_language: splitLang[0],
    browser_language_local: splitLang[1] || '',
  };
})();

const eventUrlFull = (() => {
  const addEventURL = true; // Replace with actual configuration logic if needed
  return addEventURL
    ? window.location.href.split('#')[0]
    : `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
})();

export const userAgentData = async (): Promise<UserAgentData> => {
  const userAgentData = (navigator as NavigatorWithUAData).userAgentData;
  let values: HighEntropyValues = {};
  if (userAgentData) {
    // UserAgentData is not available in Firefox
    values = await userAgentData.getHighEntropyValues([
      'architecture',
      'bitness',
      'brands',
      'mobile',
      'model',
      'platform',
      'platformVersion',
      'uaFullVersion',
      'fullVersionList',
    ]);
  }
  return {
    ch_ua: values.brands || [],
    ch_ua_arch: values.architecture || '',
    ch_ua_bitness: values.bitness || '',
    ch_ua_full_version_list: values.fullVersionList || [],
    ch_ua_mobile: values.mobile || false,
    ch_ua_model: values.model || '',
    ch_ua_platform: values.platform || '',
    ch_ua_platform_version: values.platformVersion || '',
    ch_ua_full_version: values.uaFullVersion || '',
  };
};

export const pageViewId = generateId();

export const calculateAdditionalData = () => {
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const displayWidth = window.innerWidth;
  const displayHeight = window.innerHeight;
  const previousUrl = document.referrer || '';
  const pageTitleHtml = document.title || '';
  const documentLocation = `${window.location.origin}${window.location.pathname}`;
  const consent = consentCookie() || '0';
  const date = new Date();
  const websiteType = pnpCookie() ? 'app' : 'web';
  const cookieCreationDate = getCookieCreationDate() || '';
  return {
    pageview_id: pageViewId,
    page_section: pageSection,
    page_templatetype: pageTemplateType,
    event_collection_platform: 'js',
    event_collection_version: '6.15.2',
    website_type: websiteType,
    cookie_creation_date: cookieCreationDate,
    device_screen_width: screenWidth,
    device_screen_height: screenHeight,
    device_display_width: displayWidth,
    device_display_height: displayHeight,
    device_timestamp_utc: date.getTime(),
    device_local_hour: date.getTime(),
    device_hour: date.getHours(),
    previous_url: previousUrl,
    page_title_html: pageTitleHtml,
    page_language: raboLangCookie() || 'nl',
    event_url_full: eventUrlFull,
    document_location: documentLocation,
    user_consent: consent,
    visitor_privacy_consent: consent === '1',
    visitor_privacy_mode: visitorPrivacyMode || '',
    ...browserLanguage,
  };
};

const getStepsParts = (b: AnalyticsStepData): (string | number)[] => {
  const stepParts: (string | number)[] = [];

  if (b.application_step_number) {
    stepParts.push(b.application_step_number);
  }

  if (b.application_step_name) {
    stepParts.push(b.application_step_name);
  }

  if (b.application_substep_number && b.application_substep_name) {
    stepParts.push(b.application_substep_number, b.application_substep_name);
  }
  return stepParts;
};

export const getPageNameParts = (b: ApplicationData): string[] => {
  const parts: string[] = [];
  const hostname = document.location.toString().split('?')[0].split('#')[0];
  parts.push(hostname);

  if (b.application_id) {
    parts.push(`/id=${b.application_id}`);
  }

  if (!isAnalyticsStepData(b)) {
    return parts;
  }

  const stepParts = getStepsParts(b);
  if (stepParts.length > 0) {
    parts.push(`/step=${stepParts.join('|')}`);
  }

  parts.push(
    `/type=${b.application_step_type || '0'}`,
    `/req=${b.application_step_required.toString() || 'na'}`,
  );

  return parts;
};

export const getPageName = (b: ApplicationData): string => {
  try {
    const parts = getPageNameParts(b);
    return parts.join('').replace('//', '/');
  } catch (error) {
    console.error('Error generating page name:', error);
    return '';
  }
};
